import React, { useState } from "react";
import { Box, Typography, styled, Skeleton } from "@mui/material";
import LazyLoad from "react-lazyload";
import { Link } from "react-router-dom";

const AuthorContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "24px",
  padding: "24px",
  backgroundColor: theme.palette.background.paper,
  borderRadius: "8px",
  border: `1px solid ${theme.palette.divider}`,
}));

const AuthorImageWrapper = styled(Box)({
  width: "80px",
  height: "80px",
  position: "relative",
  borderRadius: "50%",
  overflow: "hidden",
});

const AuthorImageStyled = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  transition: "opacity 0.3s ease-in-out",
});

const ImagePlaceholder = styled(Skeleton)(({ theme }) => ({
  width: "100%",
  height: "100%",
  position: "absolute",
  top: 0,
  left: 0,
  transform: "none",
  transformOrigin: "0 0",
  borderRadius: "50%",
}));

const AuthorInfo = styled(Box)({
  flex: 1,
});

const AuthorName = styled(Typography)(({ theme }) => ({
  fontSize: "1.125rem",
  fontWeight: 600,
  marginBottom: "8px",
  color: theme.palette.text.primary,
}));

const AuthorBio = styled(Typography)(({ theme, isExpanded }) => ({
  fontSize: "0.875rem",
  color: theme.palette.text.secondary,
  marginBottom: "16px",
  display: "-webkit-box",
  WebkitLineClamp: isExpanded ? "unset" : 3,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  position: "relative",
  cursor: "pointer",
  transition: "all 0.3s ease",
}));

const ReadMoreButton = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: "0.75rem",
  fontWeight: 600,
  marginTop: 4,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  transition: "color 0.2s ease",
  "&:hover": {
    color: theme.palette.primary.dark,
  },
}));

const PopularBook = styled(Box)({
  display: "flex",
  gap: "12px",
  alignItems: "flex-start",
});

const PopularBookLink = styled(Link)(({ theme }) => ({
  display: "flex",
  gap: "12px",
  alignItems: "flex-start",
  textDecoration: "none",
  transition: "transform 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    "& .book-title": {
      color: theme.palette.primary.main,
    },
    "& img": {
      transform: "scale(1.05)",
    },
  },
}));

const BookCoverWrapper = styled(Box)({
  position: "relative",
  width: "60px",
  height: "80px",
  borderRadius: "4px",
  overflow: "hidden",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
});

const BookCoverStyled = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  transition: "all 0.3s ease-in-out",
  borderRadius: "4px",
});

const BookInfo = styled(Box)({
  flex: 1,
});

const BookTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  transition: "color 0.2s ease-in-out",
}));

const LazyImage = React.memo(
  ({ src, alt, ImageComponent, WrapperComponent, placeholder }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);

    const handleLoad = () => setIsLoading(false);
    const handleError = () => {
      setIsLoading(false);
      setError(true);
    };

    return (
      <WrapperComponent>
        {isLoading && (
          <ImagePlaceholder variant="rectangular" animation="wave" />
        )}
        <ImageComponent
          src={error ? placeholder : src}
          alt={alt}
          loading="lazy"
          onLoad={handleLoad}
          onError={handleError}
          style={{ opacity: isLoading ? 0 : 1 }}
        />
      </WrapperComponent>
    );
  },
);

const ShimmerEffect = styled("span")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  animation: "shimmer 2s infinite linear",
  background: `linear-gradient(
    90deg,
    ${theme.palette.background.paper} 0%,
    ${theme.palette.grey[100]} 50%,
    ${theme.palette.background.paper} 100%
  )`,
  backgroundSize: "200% 100%",
  "@keyframes shimmer": {
    "0%": {
      backgroundPosition: "200% 0",
    },
    "100%": {
      backgroundPosition: "-200% 0",
    },
  },
}));

const SkeletonWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: theme.palette.background.paper,
    opacity: 0.5,
  },
}));

const AuthorCardSkeleton = () => (
  <AuthorContainer>
    <SkeletonWrapper>
      <Skeleton
        variant="circular"
        width={80}
        height={80}
        animation="wave"
        sx={{
          backgroundColor: "rgba(0,0,0,0.06)",
        }}
      />
      <ShimmerEffect />
    </SkeletonWrapper>

    <AuthorInfo>
      <SkeletonWrapper sx={{ mb: 1, width: "60%" }}>
        <Skeleton
          variant="text"
          height={28}
          animation="wave"
          sx={{ backgroundColor: "rgba(0,0,0,0.06)" }}
        />
        <ShimmerEffect />
      </SkeletonWrapper>

      <SkeletonWrapper sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Skeleton
            variant="text"
            height={16}
            width="95%"
            animation="wave"
            sx={{ backgroundColor: "rgba(0,0,0,0.06)" }}
          />
          <Skeleton
            variant="text"
            height={16}
            width="90%"
            animation="wave"
            sx={{ backgroundColor: "rgba(0,0,0,0.06)" }}
          />
          <Skeleton
            variant="text"
            height={16}
            width="80%"
            animation="wave"
            sx={{ backgroundColor: "rgba(0,0,0,0.06)" }}
          />
        </Box>
        <ShimmerEffect />
      </SkeletonWrapper>

      <Box sx={{ mt: 3 }}>
        <SkeletonWrapper sx={{ mb: 1, width: "40%" }}>
          <Skeleton
            variant="text"
            height={20}
            animation="wave"
            sx={{ backgroundColor: "rgba(0,0,0,0.06)" }}
          />
          <ShimmerEffect />
        </SkeletonWrapper>

        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
          <SkeletonWrapper>
            <Skeleton
              variant="rectangular"
              width={60}
              height={80}
              animation="wave"
              sx={{
                backgroundColor: "rgba(0,0,0,0.06)",
                borderRadius: 1,
              }}
            />
            <ShimmerEffect />
          </SkeletonWrapper>

          <Box sx={{ flex: 1 }}>
            <SkeletonWrapper sx={{ width: "80%" }}>
              <Skeleton
                variant="text"
                height={24}
                animation="wave"
                sx={{ backgroundColor: "rgba(0,0,0,0.06)" }}
              />
              <ShimmerEffect />
            </SkeletonWrapper>
          </Box>
        </Box>
      </Box>
    </AuthorInfo>
  </AuthorContainer>
);

const AuthorCard = ({ author, loading }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsReadMore, setNeedsReadMore] = useState(false);
  const bioRef = React.useRef(null);

  React.useEffect(() => {
    if (bioRef.current) {
      const element = bioRef.current;
      setNeedsReadMore(element.scrollHeight > element.clientHeight);
    }
  }, [author?.bio]);

  const handleBioClick = () => {
    setIsExpanded(!isExpanded);
  };

  if (loading) return <AuthorCardSkeleton />;
  if (!author) return null;

  return (
    <AuthorContainer>
      <LazyLoad
        height={80}
        offset={100}
        once
        placeholder={<Skeleton variant="circular" width={80} height={80} />}
      >
        <LazyImage
          src={author.image}
          alt={author.name}
          placeholder="/images/default-author.png"
          ImageComponent={AuthorImageStyled}
          WrapperComponent={AuthorImageWrapper}
        />
      </LazyLoad>

      <AuthorInfo>
        <AuthorName>{author.name}</AuthorName>
        <Box>
          <AuthorBio
            ref={bioRef}
            isExpanded={isExpanded}
            onClick={handleBioClick}
          >
            {author.bio || "No biography available."}
          </AuthorBio>
          {needsReadMore && (
            <ReadMoreButton onClick={handleBioClick}>
              {isExpanded ? "Read Less" : "Read More"}
            </ReadMoreButton>
          )}
        </Box>

        {author.popular_book && (
          <>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1,
                color: "text.secondary",
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Most popular book
            </Typography>

            <PopularBookLink to={`/books/${author.popular_book.id}`}>
              <LazyLoad
                height={80}
                offset={100}
                once
                placeholder={
                  <Skeleton variant="rectangular" width={60} height={80} />
                }
              >
                <LazyImage
                  src={author.popular_book.cover}
                  alt={author.popular_book.title}
                  placeholder="/images/default-book.png"
                  ImageComponent={BookCoverStyled}
                  WrapperComponent={BookCoverWrapper}
                />
              </LazyLoad>
              <BookInfo>
                <BookTitle variant="subtitle2" className="book-title">
                  {author.popular_book.title}
                </BookTitle>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    fontSize: "0.75rem",
                  }}
                >
                  Click to view details
                </Typography>
              </BookInfo>
            </PopularBookLink>
          </>
        )}
      </AuthorInfo>
    </AuthorContainer>
  );
};

export default React.memo(AuthorCard);
